import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Which screen each entry of this tab's history showed, by the entry's
// position. React Router keeps that position in `history.state.idx`, so
// walking back from the current entry finds where someone was before they
// came in, however they got here. A reload, or arriving from outside the app,
// leaves the earlier entries unknown.
const trail = new Map<number, string>();

function historyIdx(): number | null {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' ? idx : null;
}

// Notes each screen as it is shown. Mount once, inside the router.
export function useRecordTrail() {
  const location = useLocation();
  useEffect(() => {
    const idx = historyIdx();
    if (idx !== null) trail.set(idx, location.pathname);
  }, [location]);
}

function isWithin(pathname: string, placePath: string) {
  return pathname === placePath || pathname.startsWith(`${placePath}/`);
}

// How many entries back the last screen outside `placePath` is, or null when
// that isn't known.
function stepsOut(placePath: string): number | null {
  const now = historyIdx();
  if (now === null) return null;
  for (let idx = now - 1; idx >= 0; idx -= 1) {
    const pathname = trail.get(idx);
    if (pathname === undefined) return null;
    if (!isWithin(pathname, placePath)) return now - idx;
  }
  return null;
}

// The way back out of a page and everything beneath it (a class and its
// sections, a student and their tabs): to the screen the person came from, or
// up to `fallbackPath` when they opened the page directly. `toPreviousScreen`
// says which it will be, so the label can name the fallback when that's where
// it goes. `goBack` keeps its identity, so it can be published through an effect.
export function useWayBack(placePath: string, fallbackPath: string) {
  const navigate = useNavigate();
  const latest = useRef({ navigate, placePath, fallbackPath });
  useEffect(() => {
    latest.current = { navigate, placePath, fallbackPath };
  });

  const goBack = useCallback(() => {
    const { navigate: go, placePath: place, fallbackPath: fallback } = latest.current;
    const steps = stepsOut(place);
    if (steps !== null) go(-steps);
    else go(fallback);
  }, []);

  return { goBack, toPreviousScreen: stepsOut(placePath) !== null };
}
